<script lang="ts">

import { Component, Prop, Vue } from "vue-facing-decorator";
import IconComponent from "@/components/atoms/Icon.vue";
import { DateTimeZone, SearchResult } from "webshop-api";
import {
    AnalyticsEventName,
    AnalyticsStorage,
    DateTimeUtil,
    GTMTicketItemFactory,
    GTMUtils,
    IconName,
    ItemListData,
    TranslationKey,
    TranslationService
} from "webshop-modules";
import { SearchAppData } from "@/domain/search/entitites/TranslationsData.interface";

@Component({
    components: {
        IconComponent
    }
})
export default class ListItemComponent extends Vue {

    @Prop()
    public data!: SearchAppData;

    @Prop()
    public readonly item!: SearchResult;

    @Prop()
    public readonly index!: number;

    @Prop()
    public readonly listName!: string;

    public readonly IconName: typeof IconName = IconName;

    public readonly Translation: typeof TranslationKey = TranslationKey;

    public lang: string = TranslationService.getCountryCode() === 'en'
        ? 'en'
        : 'lc';

    public staticUrl: string | null = document.querySelector('body')!.getAttribute('data-static');

    public startDate = '';
    public endDate = '';

    get isHungarianLanguage(): boolean {
        return TranslationService.getLanguageCode() === 'hu';
    }

    get venue() {
        if (this.item.events) {
            if (this.item.events.length === 1) {
                return `${this.item.events[0].venue}, ${this.item.events[0].city}`;
            } else {
                let allVenues = this.item.events.map(item => {
                    return item.venue
                });
                const distinctVenues = [...new Set(allVenues)];
                if (distinctVenues.length === 1) {
                    return `${this.item.events[0].venue}, ${this.item.events[0].city}`;
                } else {
                    let allCities = this.item.events.map(item => {
                        return item.city
                    });
                    const distinctCities = [...new Set(allCities)];
                    return distinctCities.join(' / ');
                }
            }
        }
    }

    get country() {
        if (this.item.events && this.data) {
            if (this.item.events.length === 1 && this.item.events[0].lkz) {
                return this.data.countryTranslations[this.lang][this.item.events[0].lkz];
            } else {
                let allCountries = this.item.events.map(item => {
                    return this.data.countryTranslations[this.lang][item.lkz!]
                });
                const distinctCountries = [...new Set(allCountries)];

                return distinctCountries.join(' / ');
            }
        }
    }

    get earliestStartDate() {
        if (this.item.events) {
            if (this.item.events.length === 1 && this.item.events[0].timezone) {
                return DateTimeUtil
                    .getDateTimeFormatFor(TranslationService.getLanguageCode(), this.item.events[0].timezone.split(" ")[0])
                    .format(new Date((this.item.events[0].dateStart as unknown as DateTimeZone).dateTime));
            } else {
                let allStartDates = this.item.events.map(item => {
                    return (item.dateStart as unknown as DateTimeZone)
                });
                const distinctStartDates = [...new Set(allStartDates)];
                if (distinctStartDates.length === 1 && this.item.events[0].timezone) {
                    return DateTimeUtil
                        .getDateTimeFormatFor(TranslationService.getLanguageCode(), this.item.events[0].timezone.split(" ")[0])
                        .format(new Date((this.item.events[0].dateStart as unknown as DateTimeZone).dateTime));
                } else {
                    // get earliest
                    return DateTimeUtil
                        .getDateTimeFormatFor(TranslationService.getLanguageCode(), this.item.events[0].timezone!.split(" ")[0])
                        .format(new Date(Math.min.apply(null, distinctStartDates.map(e => Number(new Date(e.dateTime))))));
                }
            }
        }
    }

    get latestEndDate() {
        if (this.item.events) {
            if (this.item.events.length === 1 && this.item.events[0].timezone) {
                return DateTimeUtil
                    .getDateTimeFormatFor(TranslationService.getLanguageCode(), this.item.events[0].timezone.split(" ")[0])
                    .format(new Date((this.item.events[0].dateEnd as unknown as DateTimeZone).dateTime));
            } else {
                let allEndDates = this.item.events.map(item => {
                    return (item.dateEnd as unknown as DateTimeZone)
                });
                const distinctEndDates = [...new Set(allEndDates)];
                if (distinctEndDates.length === 1 && this.item.events[0].timezone) {
                    return DateTimeUtil
                        .getDateTimeFormatFor(TranslationService.getLanguageCode(), this.item.events[0].timezone.split(" ")[0])
                        .format(new Date(distinctEndDates[0].dateTime));
                } else {
                    // get latest
                    return DateTimeUtil
                        .getDateTimeFormatFor(TranslationService.getLanguageCode(), this.item.events[0].timezone!.split(" ")[0])
                        .format(new Date(Math.max.apply(null, distinctEndDates.map(e => Number(new Date(e.dateTime))))));
                }
            }
        }
    }

    get earliestStartDateNumeric() {
        if (this.item.events) {
            if (this.item.events.length === 1 && this.item.events[0].timezone) {
                return DateTimeUtil
                    .getDateTimeNumericFormatFor(TranslationService.getLanguageCode(), this.item.events[0].timezone.split(" ")[0])
                    .format(new Date((this.item.events[0].dateStart as unknown as DateTimeZone).dateTime));
            } else {
                let allStartDates = this.item.events.map(item => {
                    return (item.dateStart as unknown as DateTimeZone)
                });
                const distinctStartDates = [...new Set(allStartDates)];
                if (distinctStartDates.length === 1 && this.item.events[0].timezone) {
                    return DateTimeUtil
                        .getDateTimeNumericFormatFor(TranslationService.getLanguageCode(), this.item.events[0].timezone.split(" ")[0])
                        .format(new Date((this.item.events[0].dateStart as unknown as DateTimeZone).dateTime));
                } else {
                    // get earliest
                    return DateTimeUtil
                        .getDateTimeNumericFormatFor(TranslationService.getLanguageCode(), this.item.events[0].timezone!.split(" ")[0])
                        .format(new Date(Math.min.apply(null, distinctStartDates.map(e => Number(new Date(e.dateTime))))));
                }
            }
        }
    }

    get latestEndDateNumeric() {
        if (this.item.events) {
            if (this.item.events.length === 1 && this.item.events[0].timezone) {
                return DateTimeUtil
                    .getDateTimeNumericFormatFor(TranslationService.getLanguageCode(), this.item.events[0].timezone.split(" ")[0])
                    .format(new Date((this.item.events[0].dateEnd as unknown as DateTimeZone).dateTime));
            } else {
                let allEndDates = this.item.events.map(item => {
                    return (item.dateEnd as unknown as DateTimeZone)
                });
                const distinctEndDates = [...new Set(allEndDates)];
                if (distinctEndDates.length === 1 && this.item.events[0].timezone) {
                    return DateTimeUtil
                        .getDateTimeNumericFormatFor(TranslationService.getLanguageCode(), this.item.events[0].timezone.split(" ")[0])
                        .format(new Date(distinctEndDates[0].dateTime));
                } else {
                    // get latest
                    return DateTimeUtil
                        .getDateTimeNumericFormatFor(TranslationService.getLanguageCode(), this.item.events[0].timezone!.split(" ")[0])
                        .format(new Date(Math.max.apply(null, distinctEndDates.map(e => Number(new Date(e.dateTime))))));
                }
            }
        }
    }

    get startTime() {
        if (this.item.events && this.item.events[0].timezone) {
            return DateTimeUtil
                .getTimeFormatFor(TranslationService.getLanguageCode(), this.item.events[0].timezone.split(" ")[0])
                .format(new Date((this.item.events[0].dateStart as unknown as DateTimeZone).dateTime));
        }
    }

    get hasDateSpan() {
        return this.earliestStartDate !== this.latestEndDate;
    }

    public handleSelectItem() {
        GTMUtils.storeActiveItemListName(this.listName ?? '-');

        const ticketItem = GTMTicketItemFactory.fromSearchResult(this.item, this.index);
        const analyticsStore = AnalyticsStorage.getInstance();

        if (ticketItem && ticketItem.item_category === 'eventseries' && analyticsStore) {
            analyticsStore.pushDataToSessionStorage({activeEventSeries: ticketItem});
        }

        const eventData = {
            event: AnalyticsEventName.SelectItem,
            ecommerce: {
                item_list_name: this.item.title || '-',
                item_list_id: this.item.title || '-',
                items: [ticketItem],
            }
        };

        GTMUtils.pushGA4EcommerceToDataLayer<ItemListData>(eventData);
    }

}
</script>

<template>
    <li>
        <a :href="item.url"
            class="m-searchListItem"
            data-qa="search-list-item"
            v-on:click="handleSelectItem">
            <div class="m-searchListItem__block">
                <div v-if="item.image" class="a-badgeThumb">
                    <picture class="a-picture -with-ratio" style="padding-bottom: 100%;">
                        <img :src="staticUrl+item.image" alt="" role="presentation">
                    </picture>
                </div>
            </div>
            <div class="m-searchListItem__block">
                <h3 class="m-searchListItem__title">{{ item.title }}</h3>
                <address>
                    <span class="m-searchListItem__venue">{{ venue }}</span>
                </address>
                <div class="m-searchListItem__dateTime -mobile">
                    <icon-component :icon-id="IconName.CalendarSmall" class="m-searchListItem__icon"/>
                    <template v-if="hasDateSpan">
                        <span class="m-searchListItem__dateItem">{{ earliestStartDateNumeric }}</span>
                        <span>-</span>
                        <span class="m-searchListItem__dateItem">{{ latestEndDateNumeric }}</span>
                    </template>
                    <template v-else>
                        <span class="m-searchListItem__dateItem">{{ earliestStartDate }}</span>
                        <span v-translate="{ key: Translation.Date_at_label }" class="m-searchListItem__dateItem"></span>
                        <span class="m-searchListItem__dateItem">{{ startTime }}</span>
                    </template>
                </div>
            </div>
            <div class="m-searchListItem__block">
                <template v-if="hasDateSpan">
                    <template v-if="isHungarianLanguage">
                        <div class="m-searchListItem__dateTime -is-alternative">
                            <span class="m-searchListItem__dateItem">{{ earliestStartDate }}</span>
                            <span class="m-searchListItem__dateItem">{{ latestEndDate }}</span>
                        </div>
                        <div class="m-searchListItem__labels -is-alternative">
                            <div class="m-searchListItem__label">
                                &ndash; <span v-translate="{ key: Translation.Date_from_label }"></span>
                            </div>
                            <div class="m-searchListItem__label">
                                &ndash; <span v-translate="{ key: Translation.Date_to_label }"></span>
                            </div>
                        </div>
                    </template>

                    <template v-else>
                        <div class="m-searchListItem__labels">
                            <span class="m-searchListItem__label" v-translate="{ key: Translation.Date_from_label }"></span>
                            <span class="m-searchListItem__label" v-translate="{ key: Translation.Date_to_label }"></span>
                        </div>
                        <div class="m-searchListItem__dateTime">
                            <span class="m-searchListItem__dateItem">{{ earliestStartDate }}</span>
                            <span class="m-searchListItem__dateItem">{{ latestEndDate }}</span>
                        </div>
                    </template>
                </template>

                <template v-else>
                    <template v-if="isHungarianLanguage">
                        <div class="m-searchListItem__dateTime -is-alternative">
                            <span class="m-searchListItem__dateItem">{{ earliestStartDate }}</span>
                            <span class="m-searchListItem__dateItem">{{ startTime }}</span>
                        </div>

                        <div class="m-searchListItem__labels -is-alternative">
                            <span class="m-searchListItem__label" v-translate="{ key: Translation.Date_label }"></span>
                            <span class="m-searchListItem__label" v-translate="{ key: Translation.Time_label }"></span>
                        </div>
                    </template>

                    <template v-else>
                        <div class="m-searchListItem__labels">
                            <span class="m-searchListItem__label" v-translate="{ key: Translation.Date_label }"></span>
                            <span class="m-searchListItem__label" v-translate="{ key: Translation.Time_label }"></span>
                        </div>
                        <div class="m-searchListItem__dateTime">
                            <span class="m-searchListItem__dateItem">{{ earliestStartDate }}</span>
                            <span class="m-searchListItem__dateItem">{{ startTime }}</span>
                        </div>
                    </template>
                </template>
            </div>
            <div class="m-searchListItem__block">
                <button v-translate="{ key: Translation.List_button_label }" class="a-btnCta" type="button">Tickets</button>
                <icon-component :icon-id="IconName.Chevron" class="m-searchListItem__iconCta"/>
            </div>
        </a>
    </li>
</template>

<style scoped>
</style>
